<template>
    <div class="home">
      <div class="header">
      <batch-actions 
        :selected-issues="selectedIssues"
        :project-id="filters.project_id"
        @refresh="fetchIssues"
      />
        <div class="header-right">
          <el-select 
            v-model="filters.project_id" 
            clearable 
            placeholder="选择项目"
            @change="applyFilters"
            style="width: 200px; margin-right: 10px;"
          >
            <el-option
              v-for="project in projects"
              :key="project.id"
              :label="project.name"
              :value="project.id">
            </el-option>
          </el-select>
          <div class="user-info" v-if="user">
            Welcome, {{ user.name }} 
            <el-button @click="handleLogout" size="small">Logout</el-button>
          </div>
        </div>
      </div>
      <el-button @click="filterVisible = !filterVisible" type="primary" size="small">
        {{ filterVisible ? '隐藏筛选' : '显示筛选' }}
      </el-button>
      <el-collapse-transition>
        <div v-show="filterVisible" class="filter-container">
          <el-form :inline="true" :model="filters" class="filter-form">
            <!-- Project filter removed from here and moved to header -->
            <el-form-item label="状态">
              <el-select v-model="filters.status_id" clearable placeholder="请选择状态">
                <el-option
                  v-for="status in statuses"
                  :key="status.id"
                  :label="status.name"
                  :value="status.id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="优先级">
              <el-select v-model="filters.priority_id" clearable placeholder="请选择优先级">
                <el-option
                  v-for="priority in priorities"
                  :key="priority.id"
                  :label="priority.name"
                  :value="priority.id">
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="指派给">
              <el-select 
                v-model="filters.assigned_to_id" 
                clearable 
                placeholder="请选择指派人"
                filterable
              >
                <el-option
                  v-for="user in users"
                  :key="user.id"
                  :label="user.lastname + user.firstname"
                  :value="user.id">
                </el-option>
                <template #prefix>
                  <span v-if="filters.assigned_to_id">
                    {{ getUserName(filters.assigned_to_id) }}
                  </span>
                </template>
              </el-select>
            </el-form-item>
            <el-form-item label="创建时间">
              <el-date-picker
                v-model="filters.created_on"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期">
              </el-date-picker>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="applyFilters">筛选</el-button>
              <el-button @click="resetFilters">重置</el-button>
            </el-form-item>
          </el-form>
        </div>
      </el-collapse-transition>
      <issue-list 
        :issues="issues" 
        :loading="loading"
        @selection-change="handleSelectionChange"
        @refresh="fetchIssues"
      />
      <el-pagination
        class="pagination"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        :total="totalItems"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </template>
  
  <script>
  import IssueList from '@/components/IssueList.vue'
  import BatchActions from '@/components/BatchActions.vue'
  
  export default {
    name: 'Home',
    components: {
      IssueList,
      BatchActions
    },
    data() {
      return {
        user: JSON.parse(localStorage.getItem('user')),
        issues: [],
        loading: false,
        selectedIssues: [],
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        filters: {
          project_id: null,
          status_id: null,
          priority_id: null,
          assigned_to_id: null,
          created_on_start: null,
          created_on_end: null
        },
        filterVisible: false,
        projects: [],
        issueTypes: [],
        statuses: [],
        priorities: [],
        users: []
      }
    },
    created() {
      if (!this.user) {
        this.$router.push('/login')
      }
      this.fetchIssues()
      this.fetchFilterOptions()
    },
    methods: {
      handleLogout() {
        localStorage.removeItem('user')
        this.$router.push('/login')
      },
      async fetchIssues() {
        this.loading = true
        try {
          const params = {
            limit: this.pageSize,
            offset: (this.currentPage - 1) * this.pageSize,
            project_id: this.filters.project_id,
            status_id: this.filters.status_id,
            priority_id: this.filters.priority_id,
            assigned_to_id: this.filters.assigned_to_id
          }

          // Handle date range filter
          if (this.filters.created_on && this.filters.created_on.length === 2) {
            params.created_on_start = this.filters.created_on[0].toISOString().split('T')[0]
            params.created_on_end = this.filters.created_on[1].toISOString().split('T')[0]
          }
          const response = await this.$axios.get('/api/issues', {
            params,
            headers: {
              'X-User-Id': this.user.id,
              'X-User-Name': this.user.name
            }
          })
          if (response.data.success) {
            this.issues = response.data.data.issues || []
            this.totalItems = response.data.data.total_count || 0
          } else {
            this.$message.error(response.data.error || '获取问题单失败')
          }
        } catch (error) {
          this.$message.error('网络错误: ' + error.message)
        } finally {
          this.loading = false
        }
      },
      handleSizeChange(val) {
        this.pageSize = val
        this.fetchIssues()
      },
      handleCurrentChange(val) {
        this.currentPage = val
        this.fetchIssues()
      },
      handleSelectionChange(selection) {
        this.selectedIssues = selection.map(item => item.id)
      },
      async fetchFilterOptions() {
        try {
          // Fetch projects
          const projectsRes = await this.$axios.get('/api/projects', {
            headers: {
              'X-User-Id': this.user.id,
              'X-User-Name': this.user.name
            }
          })
          if (projectsRes.data.success) {
            this.projects = projectsRes.data.data.projects || []
          }

          // Fetch statuses
          const statusesRes = await this.$axios.get('/api/statuses', {
            headers: {
              'X-User-Id': this.user.id,
              'X-User-Name': this.user.name
            }
          })
          if (statusesRes.data.success) {
            this.statuses = statusesRes.data.data.issue_statuses || []
          }

          // Fetch trackers (issue types)
          const trackersRes = await this.$axios.get('/api/trackers', {
            headers: {
              'X-User-Id': this.user.id,
              'X-User-Name': this.user.name
            }
          })
          if (trackersRes.data.success) {
            this.issueTypes = trackersRes.data.data.trackers || []
          }

          // Fetch priorities
          const prioritiesRes = await this.$axios.get('/api/priorities', {
            headers: {
              'X-User-Id': this.user.id,
              'X-User-Name': this.user.name
            }
          })
          if (prioritiesRes.data.success) {
            this.priorities = prioritiesRes.data.data.issue_priorities || []
          }

          // Fetch users
          const usersRes = await this.$axios.get('/api/users', {
            headers: {
              'X-User-Id': this.user.id,
              'X-User-Name': this.user.name
            }
          })
          if (usersRes.data.success) {
            this.users = usersRes.data.data.users || []
          }
        } catch (error) {
          this.$message.error('加载筛选选项失败: ' + error.message)
        }
      },
      applyFilters() {
        this.currentPage = 1 // Reset to first page when applying new filters
        this.fetchIssues()
      },
      resetFilters() {
        this.filters = {
          project_id: null,
          status_id: null,
          priority_id: null,
          assigned_to_id: null,
          created_on_start: null,
          created_on_end: null
        }
        this.currentPage = 1
        this.fetchIssues()
      },
      getUserName(userId) {
        const user = this.users.find(u => u.id === userId)
        const ret = user ? user.lastname + user.firstname : '';
        console.log(userId + '  ' + ret);
        return ret;
      }
    }
  }
  </script>

  <style scoped>
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  .header-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .user-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .filter-container {
    margin-bottom: 20px;
  }
  </style>



// WEBPACK FOOTER //
// src/views/Home.vue